/**
 * Admin Ecosystem HR
 * -------------------
 * One unified directory of every staff person across every scope
 * (schools, events, churches, ministries). Tenant-scoped for tenant admins;
 * full cross-scope visibility for super-admin.
 */
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Users, Search, Sparkles, Building2, Briefcase, RefreshCw, Loader2, GitMerge, CheckCircle, UserPlus, Edit2, Trash2, ChevronRight, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import { MediaUpload } from '@/components/MediaUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const scopeBadge = (scopeType) => {
  const map = {
    school: 'bg-indigo-100 text-indigo-700',
    church: 'bg-purple-100 text-purple-700',
    event: 'bg-amber-100 text-amber-700',
    ministry: 'bg-rose-100 text-rose-700',
    org: 'bg-slate-100 text-slate-700',
  };
  return map[scopeType] || 'bg-slate-100 text-slate-700';
};

function KpiCard({ title, value, icon: Icon, tone = 'indigo', sub }) {
  const tones = {
    indigo: 'from-indigo-500/15 to-indigo-500/5 text-indigo-600',
    emerald: 'from-emerald-500/15 to-emerald-500/5 text-emerald-600',
    amber: 'from-amber-500/15 to-amber-500/5 text-amber-600',
    rose: 'from-rose-500/15 to-rose-500/5 text-rose-600',
  };
  return (
    <Card className={`border bg-gradient-to-br ${tones[tone]}`} data-testid={`hr-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
          </div>
          <div className={`p-2 rounded-lg bg-white/60 ${tones[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DuplicateGroup({ group, onMerge, pending }) {
  const [keepId, setKeepId] = useState(group.people[0]?._id || '');
  const [checked, setChecked] = useState(() => new Set(group.people.slice(1).map((p) => p._id)));

  const toggle = (id) => {
    if (id === keepId) return;
    const n = new Set(checked); n.has(id) ? n.delete(id) : n.add(id); setChecked(n);
  };
  const toMerge = Array.from(checked).filter((id) => id !== keepId);

  return (
    <div className="border rounded-lg p-4 bg-slate-50" data-testid={`duplicate-group-${group.slug}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-semibold text-slate-900">{group.display_name} <span className="text-xs text-slate-400 ml-2">({group.count} candidates)</span></p>
        <Button
          size="sm"
          disabled={pending || toMerge.length === 0}
          onClick={() => onMerge({ keep_id: keepId, merge_ids: toMerge })}
          data-testid={`merge-btn-${group.slug}`}
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <GitMerge className="h-4 w-4 mr-1" />}
          Merge {toMerge.length} into master
        </Button>
      </div>
      <div className="space-y-2">
        {group.people.map((p) => (
          <label
            key={p._id}
            className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-all ${keepId === p._id ? 'bg-emerald-50 border-emerald-400' : 'bg-white hover:bg-slate-100'}`}
            data-testid={`dup-row-${p._id}`}
          >
            <input
              type="radio"
              name={`keep-${group.slug}`}
              checked={keepId === p._id}
              onChange={() => { setKeepId(p._id); const n = new Set(checked); n.delete(p._id); setChecked(n); }}
              className="h-4 w-4"
            />
            <span className="text-xs font-medium text-emerald-700 w-14">{keepId === p._id ? 'Master' : ''}</span>
            <div className="flex-1">
              <p className="text-sm font-medium">{p.full_name}</p>
              <p className="text-xs text-slate-500">{p.email || '—'} · {p.phone || 'no phone'}</p>
            </div>
            <Badge variant="outline" className="text-xs">{p.assignments_count} role{p.assignments_count === 1 ? '' : 's'}</Badge>
            {keepId !== p._id && (
              <input type="checkbox" checked={checked.has(p._id)} onChange={() => toggle(p._id)} className="h-4 w-4" />
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function AdminEcosystemHR() {
  const { token } = useAuthStore();
  const hdrs = () => ({ Authorization: `Bearer ${token || getToken()}`, 'Content-Type': 'application/json' });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  const BLANK = {
    full_name: '', email: '', phone: '', title: '',
    employee_id: '', department: '', designation: '',
    date_of_joining: '', emergency_contact: '',
    nationality: '', gender: '', photo_url: '', isNew: true,
  };

  const CROSS_MODULES = [
    { id: 'bookshop', name: 'Bookshop team', to: '/admin/shop', desc: 'Invites, roles, permissions for bookshop staff' },
    { id: 'events', name: 'Event team', to: '/admin/events', desc: 'Event volunteers, ushers, checkin roles' },
    { id: 'fwtv', name: 'Faith Works TV', to: '/admin/fwtv', desc: 'Producers, editors, transcribers' },
    { id: 'school', name: 'School HR', to: '/admin/school', desc: 'Teachers, admin staff, HR intelligence' },
    { id: 'church', name: 'Church module', to: '/admin/church', desc: 'Elders, ministers, ministry heads' },
    { id: 'housing', name: 'Housing staff', to: '/admin/staff-roles', desc: 'Room stewards, housekeeping, service desk' },
    { id: 'security', name: 'Security staff', to: '/admin/staff-roles', desc: 'Guards, patrol officers' },
    { id: 'dining', name: 'Dining staff', to: '/admin/staff-roles', desc: 'Kitchen and POS staff' },
  ];

  const { data: summary, refetch: refetchSum } = useQuery({
    queryKey: ['eco-hr-summary'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/ecosystem-hr/summary`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const { data: people = { total: 0, items: [] }, isLoading, refetch } = useQuery({
    queryKey: ['eco-hr-persons', search],
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: '100' });
      if (search) qs.set('search', search);
      const r = await fetch(`${API_URL}/api/ecosystem-hr/persons?${qs}`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const runBackfill = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_URL}/api/ecosystem-hr/backfill`, { method: 'POST', headers: hdrs() });
      if (!r.ok) throw new Error('Backfill failed');
      return r.json();
    },
    onSuccess: (d) => {
      const total = Object.values(d.migrated || {}).reduce((a, b) => a + b, 0);
      toast.success(`Merged ${total} legacy rows into the unified HR.`);
      refetchSum(); refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const multiScopePeople = useMemo(
    () => (people.items || []).filter((p) => (p.assignments || []).length > 1),
    [people]
  );

  // ==== DUPLICATES ====
  const { data: dupData, isLoading: loadingDup, refetch: refetchDup } = useQuery({
    queryKey: ['eco-hr-duplicates'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/ecosystem-hr/duplicates`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const mergeMutation = useMutation({
    mutationFn: async ({ keep_id, merge_ids }) => {
      const r = await fetch(`${API_URL}/api/ecosystem-hr/merge`, {
        method: 'POST', headers: { ...hdrs(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ keep_id, merge_ids }),
      });
      if (!r.ok) throw new Error('Merge failed');
      return r.json();
    },
    onSuccess: (d) => {
      toast.success(`Merged ${d.deleted_persons} duplicate${d.deleted_persons === 1 ? '' : 's'} · ${d.moved_assignments} assignment${d.moved_assignments === 1 ? '' : 's'} reassigned.`);
      refetchSum(); refetch(); refetchDup();
    },
    onError: (e) => toast.error(e.message),
  });

  // Person create/edit/delete mutations
  const savePerson = useMutation({
    mutationFn: async (p) => {
      const { isNew, _id, assignments, ...body } = p;
      const url = isNew ? `${API_URL}/api/ecosystem-hr/persons`
                          : `${API_URL}/api/ecosystem-hr/persons/${_id}`;
      const method = isNew ? 'POST' : 'PATCH';
      const r = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(body) });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || 'Save failed');
      }
      return r.json();
    },
    onSuccess: () => {
      toast.success(editing?.isNew ? 'Person created' : 'Saved');
      setShowDialog(false); setEditing(null); refetch(); refetchSum();
    },
    onError: (e) => toast.error(e.message),
  });

  const deletePerson = useMutation({
    mutationFn: async (id) => {
      const r = await fetch(`${API_URL}/api/ecosystem-hr/persons/${id}`,
                              { method: 'DELETE', headers: hdrs() });
      if (!r.ok) throw new Error('Delete failed');
      return r.json();
    },
    onSuccess: () => { toast.success('Deleted'); refetch(); refetchSum(); },
    onError: (e) => toast.error(e.message),
  });

  const openNew = () => { setEditing({ ...BLANK }); setShowDialog(true); };
  const openEdit = (p) => { setEditing({ ...BLANK, ...p, isNew: false }); setShowDialog(true); };
  const setField = (k, v) => setEditing((prev) => ({ ...prev, [k]: v }));

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="ecosystem-hr-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <span className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                <Users className="h-6 w-6" />
              </span>
              Ecosystem HR
            </h1>
            <p className="text-slate-500 mt-1">
              One person, many roles. Every staff record across schools, events, churches, and ministries — deduped and linked.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={openNew}
              data-testid="hr-new-person-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" /> New person
            </Button>
            <Button
              variant="outline"
              onClick={() => runBackfill.mutate()}
              disabled={runBackfill.isPending}
              data-testid="hr-backfill-btn"
            >
              {runBackfill.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Re-run Backfill
            </Button>
            <Button variant="outline" size="icon" onClick={() => refetch()} data-testid="hr-refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KpiCard title="Unique Persons" value={summary?.total_persons ?? 0} icon={Users} tone="indigo" sub="one human, one record" />
          <KpiCard title="Active Assignments" value={summary?.active_assignments ?? 0} icon={Briefcase} tone="emerald" />
          <KpiCard title="Multi-scope" value={summary?.multi_scope_persons ?? 0} icon={Sparkles} tone="amber" sub="people with 2+ roles" />
          <KpiCard title="Scopes Covered" value={Object.keys(summary?.by_scope || {}).length} icon={Building2} tone="rose" />
        </div>

        {/* By-scope breakdown */}
        {summary?.by_scope && Object.keys(summary.by_scope).length > 0 && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Active assignments by scope</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(summary.by_scope).map(([scope, roles]) => (
                <div key={scope}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={`${scopeBadge(scope)} capitalize`}>{scope}</Badge>
                    <span className="text-xs text-slate-500">{Object.values(roles).reduce((a, b) => a + b, 0)} assignments</span>
                  </div>
                  <div className="flex flex-wrap gap-2 ml-1">
                    {Object.entries(roles).map(([role, count]) => (
                      <Badge key={role} variant="outline" className="text-xs">
                        <span className="capitalize">{role.replace(/_/g, ' ')}</span>
                        <span className="ml-2 font-bold text-slate-700">{count}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Tabs: Directory + Duplicates + Cross-module */}
        <Tabs defaultValue="directory" className="w-full">
          <TabsList>
            <TabsTrigger value="directory" data-testid="hr-tab-directory">Directory</TabsTrigger>
            <TabsTrigger value="duplicates" data-testid="hr-tab-duplicates">
              Duplicates
              {dupData?.total_groups > 0 && <Badge className="ml-2 bg-amber-500 hover:bg-amber-500">{dupData.total_groups}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="modules" data-testid="hr-tab-modules">Cross-module hub</TabsTrigger>
          </TabsList>

          <TabsContent value="directory" className="mt-4">
        {/* Search + Directory */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-500" />
              Directory
              <span className="text-xs text-slate-400">({people.total})</span>
            </CardTitle>
            <div className="relative w-72">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name, email, phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="hr-search-input"
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Assignments</TableHead>
                    <TableHead>Scopes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(people.items || []).length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No people found.</TableCell></TableRow>
                  ) : people.items.map((p) => (
                    <TableRow
                      key={p._id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => setSelected(p)}
                      data-testid={`hr-person-${p._id}`}
                    >
                      <TableCell className="font-medium">
                        {p.full_name}
                        {p.title && <span className="text-xs text-slate-400 ml-1">· {p.title}</span>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {p.employee_id && <Badge variant="outline" className="mr-1">{p.employee_id}</Badge>}
                        {p.department || '—'}
                        {p.designation && <span className="text-slate-400"> · {p.designation}</span>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        <div>{p.email || '—'}</div>
                        <div className="text-xs text-slate-400">{p.phone || '—'}</div>
                      </TableCell>
                      <TableCell>
                        {(p.assignments || []).length > 1
                          ? <Badge className="bg-amber-500 hover:bg-amber-500">{p.assignments.length} roles</Badge>
                          : <span className="text-sm text-slate-500">{(p.assignments || []).length}</span>
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(p.assignments || []).slice(0, 3).map((a) => (
                            <Badge key={a._id} className={`${scopeBadge(a.scope_type)} text-xs capitalize`}>
                              {a.scope_type} · {String(a.role).replace(/_/g, ' ')}
                            </Badge>
                          ))}
                          {(p.assignments || []).length > 3 && (
                            <Badge variant="outline" className="text-xs">+{p.assignments.length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost"
                                onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                                data-testid={`hr-edit-${p._id}`}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm(`Delete ${p.full_name}? Assignments will be removed.`)) {
                                    deletePerson.mutate(p._id);
                                  }
                                }}
                                data-testid={`hr-del-${p._id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="duplicates" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitMerge className="h-5 w-5 text-amber-500" /> Suggested merges
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  People with identical names. Pick the master record to keep, tick the duplicates to merge into it. Assignments move across, PII fills in, duplicate rows are deleted.
                </p>
              </CardHeader>
              <CardContent>
                {loadingDup ? (
                  <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : !dupData || dupData.total_groups === 0 ? (
                  <div className="py-12 text-center text-slate-500 flex flex-col items-center gap-2">
                    <CheckCircle className="h-8 w-8 text-emerald-500" />
                    No duplicate candidates right now.
                  </div>
                ) : (
                  <div className="space-y-5">
                    {dupData.groups.map((g) => <DuplicateGroup key={g.slug} group={g} onMerge={mergeMutation.mutate} pending={mergeMutation.isPending} />)}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="modules" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 className="h-4 w-4 text-indigo-500" />
                  Where each module manages its own team
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Ecosystem HR is the master directory. Each operational module keeps its own team,
                  roles and permissions closer to its data — jump to any module below.
                </p>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-3">
                {CROSS_MODULES.map((m) => (
                  <a key={m.id} href={m.to}
                     className="border rounded-lg p-3 bg-slate-50 hover:bg-white flex items-start gap-3 transition"
                     data-testid={`cross-module-${m.id}`}>
                    <UserCog className="h-4 w-4 mt-0.5 text-indigo-500" />
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{m.name}</div>
                      <div className="text-xs text-slate-500">{m.desc}</div>
                      <code className="text-[10px] text-slate-400">{m.to}</code>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </a>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Multi-scope callout */}
        {multiScopePeople.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-amber-900">
                <Sparkles className="h-4 w-4" /> {multiScopePeople.length} people wearing multiple hats
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-amber-900/80">
              These people hold active roles across more than one scope. Previously they were duplicated in separate tables.
            </CardContent>
          </Card>
        )}

        {/* Person detail modal-ish side panel */}
        {selected && (
          <Card className="fixed right-6 bottom-6 w-full max-w-[380px] shadow-2xl z-50" data-testid="hr-person-detail">
            <CardHeader className="pb-2 flex flex-row items-start justify-between">
              <div>
                <CardTitle className="text-base">{selected.full_name}</CardTitle>
                <p className="text-xs text-slate-500 mt-1">{selected.email || selected.phone}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>×</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs font-semibold text-slate-700">Assignments</p>
              {(selected.assignments || []).map((a) => (
                <div key={a._id} className="p-3 rounded-lg bg-slate-50 border text-sm">
                  <div className="flex items-center justify-between">
                    <Badge className={`${scopeBadge(a.scope_type)} capitalize`}>{a.scope_type}</Badge>
                    <Badge variant={a.status === 'active' ? 'default' : 'outline'} className="text-xs">{a.status}</Badge>
                  </div>
                  <p className="mt-1 font-medium capitalize">{String(a.role).replace(/_/g, ' ')}</p>
                  <p className="text-xs text-slate-500">{a.employment_type}</p>
                  <p className="text-[10px] font-mono text-slate-400 mt-1">{a.scope_id}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Person editor dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.isNew ? 'Create person' : 'Edit person'}</DialogTitle>
            <DialogDescription>
              Core identity + HR-extended employee fields (department, designation,
              employee ID, join date, emergency contact).
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid md:grid-cols-2 gap-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
              <FldEditor label="Full name *" value={editing.full_name} onChange={(v) => setField('full_name', v)} testid="fld-full-name" wide />
              <FldEditor label="Title (Dr / Rev / Mrs)" value={editing.title} onChange={(v) => setField('title', v)} testid="fld-title" />
              <FldEditor label="Employee ID" value={editing.employee_id} onChange={(v) => setField('employee_id', v)} testid="fld-empid" placeholder="EMP-0001" />
              <FldEditor label="Email" value={editing.email} onChange={(v) => setField('email', v)} testid="fld-email" />
              <FldEditor label="Phone" value={editing.phone} onChange={(v) => setField('phone', v)} testid="fld-phone" />
              <FldEditor label="Department" value={editing.department} onChange={(v) => setField('department', v)} testid="fld-department" />
              <FldEditor label="Designation" value={editing.designation} onChange={(v) => setField('designation', v)} testid="fld-designation" />
              <FldEditor label="Date of joining" value={editing.date_of_joining} onChange={(v) => setField('date_of_joining', v)} testid="fld-doj" placeholder="YYYY-MM-DD" />
              <FldEditor label="Emergency contact" value={editing.emergency_contact} onChange={(v) => setField('emergency_contact', v)} testid="fld-emergency" wide />
              <FldEditor label="Nationality" value={editing.nationality} onChange={(v) => setField('nationality', v)} testid="fld-nationality" />
              <FldEditor label="Gender" value={editing.gender} onChange={(v) => setField('gender', v)} testid="fld-gender" />
              <div className="col-span-2">
                <MediaUpload value={editing.photo_url} onChange={(url) => setField('photo_url', url)} purpose="avatar" label="Photo" testId="fld-photo" previewClassName="h-16" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="person-cancel">Cancel</Button>
            <Button onClick={() => savePerson.mutate(editing)}
                     disabled={savePerson.isPending}
                     data-testid="person-save">
              {savePerson.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {editing?.isNew ? 'Create' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

const FldEditor = ({ label, value, onChange, testid, placeholder = '', wide = false }) => (
  <div className={wide ? 'md:col-span-2' : ''}>
    <Label className="text-xs text-slate-600">{label}</Label>
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder} className="mt-1" data-testid={testid} />
  </div>
);
